import { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import {
  BottomActionArea,
  BottomActionButton,
  FormHeader,
  SnackBar,
  NavigateBeforeButton,
  FormBody,
  FormContentContainer,
} from "../../../components";
import { StepLabel } from "./../styles";
import { FormContainer } from "../../../styles/styled";
import styled from "@emotion/styled";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UseMutateFunction, useMutation } from "@tanstack/react-query";
import { apiGateway } from "@contexts";
import { AxiosError } from "axios";
import {
  Question,
  QuestionsChallengeActionError,
  SnackbarState,
  Step,
  ValidationChallengePayload,
} from "../../../interfaces";
import { theme } from "../../../config";
import { millisecondsInSecond } from "date-fns";
import {
  ActionBlockModal,
  ActionRetryModal,
  UnableToVerifyResponsesModal,
} from "./index";
import {
  ErrorType,
  onQuestionChallengeAxiosErrorInterpreter,
} from "../hooks/onQuestionChallengeError";
import { getQuestionsChallengeErrorDisplay } from "../constants/questionsChallengeErrorMessages";

interface Props {
  questions?: Question[];
  setStep: React.Dispatch<React.SetStateAction<Step>>;
  createValidationChallenge: UseMutateFunction<
    Question[],
    Error,
    ValidationChallengePayload,
    unknown
  >;
  isCreatingValidationChallenge: boolean;
  validationChallengePayload: ValidationChallengePayload;
}

interface Answer {
  questionId: string;
  answerId: string;
}

const CenteredContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  margin: "auto",
  gap: "1rem",
});

const PaddedRadioGroup = styled(RadioGroup)({
  paddingInline: "16px",
});

const Title = styled(Typography)({
  fontSize: "1rem",
  fontWeight: 400,
});

export const QuestionsChallengeForm: React.FC<Props> = ({
  questions,
  setStep,
  createValidationChallenge,
  isCreatingValidationChallenge,
  validationChallengePayload,
}) => {
  const [searchParams] = useSearchParams();
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockModalContent, setBlockModalContent] = useState<{
    title: string;
    description?: string | React.ReactNode;
  }>({ title: "" });
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [retryModalContent, setRetryModalContent] = useState<{
    title: string;
    description?: string | React.ReactNode;
  }>({ title: "" });
  const [
    showUnableToVerifyResponsesModal,
    setShowUnableToVerifyResponsesModal,
  ] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { isValid },
    reset,
  } = useForm({ mode: "onChange" });
  const [snackbarState, setSnackbarState] = useState<SnackbarState>({
    isVisible: false,
    severity: "error",
  });
  const [snackbarErrorMessage, setSnackbarErrorMessage] = useState<string>("");

  const { mutate: validateChallenge, isPending } = useMutation({
    mutationFn: (answers: Answer[]) =>
      apiGateway<void, ErrorType>(
        {
          method: "post",
          url: "/enrolment/questions-challenge/verify",
          data: { answers },
        },
        onQuestionChallengeAxiosErrorInterpreter,
      ),
    onSuccess: () => {
      const requestId = searchParams.get("requestId");
      const queryParams = requestId ? `?requestId=${requestId}` : "";
      navigate(`../crear-pin${queryParams}`);
    },
    onError: async (err) => {
      const handleActionError = (error: QuestionsChallengeActionError) => {
        switch (error.action) {
          case "block":
            setBlockModalContent({
              title: error.name,
              description: error.description,
            });
            setShowBlockModal(true);
            break;
          case "retry":
            setRetryModalContent({
              title: error.name,
              description: error.description,
            });
            setShowRetryModal(true);
            break;
          case "incompleteData":
            setBlockModalContent({
              title: error.name,
              description: error.description,
            });
            setShowBlockModal(true);
            break;
          case "invalidInput":
            setSnackbarErrorMessage(error.name);
            reset();
            setSnackbarState((prevState) => ({
              ...prevState,
              isVisible: true,
            }));
            createValidationChallenge(validationChallengePayload, {
              onError(innerError) {
                if (innerError instanceof QuestionsChallengeActionError) {
                  handleActionError(innerError);
                } else {
                  setShowUnableToVerifyResponsesModal(true);
                }
              },
            });
            break;
        }
      };

      if (err instanceof QuestionsChallengeActionError) {
        handleActionError(err);
        return;
      }
      // Fallback: e.g. invalid answers without action (legacy) or other errors
      const errorName =
        err instanceof AxiosError
          ? (err.response?.data as { error?: { name?: string } } | undefined)
              ?.error?.name
          : undefined;
      setSnackbarErrorMessage(
        getQuestionsChallengeErrorDisplay(errorName ?? "unexpectedError").name,
      );
      reset();
      setSnackbarState((prevState) => ({ ...prevState, isVisible: true }));
      createValidationChallenge(validationChallengePayload, {
        onError(innerError) {
          if (innerError instanceof QuestionsChallengeActionError) {
            handleActionError(innerError);
          } else {
            setShowUnableToVerifyResponsesModal(true);
          }
        },
      });
    },
  });
  const navigate = useNavigate();
  const handleBack = useCallback(() => {
    if (showBlockModal || showRetryModal) {
      return navigate("/app/solicitudes");
    }
    setStep("ciDocForm");
  }, [navigate, setStep, showBlockModal, showRetryModal]);

  const onSubmit = async (data: Record<string, string>) => {
    const answers: Answer[] = Object.entries(data).map(([key, value]) => ({
      questionId: key,
      answerId: value,
    }));
    validateChallenge(answers);
  };
  return (
    <>
      <FormHeader
        title="Verificación de identidad"
        titleColor="white"
        left={<NavigateBeforeButton iconColor="white" onClick={handleBack} />}
      />
      <FormContentContainer disableGutters>
        <FormBody>
          {isCreatingValidationChallenge && (
            <CenteredContainer>
              <Title>Buscando nuevas preguntas de seguridad</Title>
              <CircularProgress
                size={24}
                style={{ color: theme.pallete.text.primary }}
              />
            </CenteredContainer>
          )}
          {!isCreatingValidationChallenge && (
            <>
              <StepLabel alignSelf="flex-start">Paso 3 de 3</StepLabel>
              <Typography>Responde estas preguntas de seguridad</Typography>
              <FormContainer onSubmit={handleSubmit(onSubmit)}>
                {questions?.map(({ questionId, questionText, options }) => (
                  <div key={questionId}>
                    <Typography fontWeight={500} marginBottom={"16px"}>
                      {questionId}. {questionText}
                    </Typography>
                    <Controller
                      control={control}
                      name={`${questionId}`}
                      defaultValue=""
                      rules={{ required: true }}
                      render={({ field }) => (
                        <PaddedRadioGroup {...field}>
                          {options.map(({ optionId, optionText }) => (
                            <FormControlLabel
                              key={`${questionId}-${optionId}`}
                              value={optionId}
                              control={<Radio size="small" />}
                              label={optionText}
                            />
                          ))}
                        </PaddedRadioGroup>
                      )}
                    />
                  </div>
                ))}
                <BottomActionArea>
                  <BottomActionButton
                    type="submit"
                    disabled={!isValid || isPending}
                  >
                    {isPending ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Siguiente"
                    )}
                  </BottomActionButton>
                </BottomActionArea>
              </FormContainer>
            </>
          )}
        </FormBody>
      </FormContentContainer>
      <SnackBar
        snackBarState={{
          open: snackbarState.isVisible,
          type: snackbarState.severity,
        }}
        onClose={() =>
          setSnackbarState((prevState) => ({ ...prevState, isVisible: false }))
        }
        autoHideDuration={5 * millisecondsInSecond}
        alerts={{
          error: snackbarErrorMessage,
        }}
      />
      <ActionBlockModal
        isOpen={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        title={blockModalContent.title}
        description={blockModalContent.description}
      />
      <ActionRetryModal
        isOpen={showRetryModal}
        onClose={() => setShowRetryModal(false)}
        title={retryModalContent.title}
        description={retryModalContent.description}
      />
      <UnableToVerifyResponsesModal
        isOpen={showUnableToVerifyResponsesModal}
        onClose={() => setShowUnableToVerifyResponsesModal(false)}
      />
    </>
  );
};
